// Columna izquierda del explorer (`.col-filters` del mockup), en el mismo orden que él:
// Búsqueda · Urgencia · Estado · Motivo del pending · Producto de interés · Próximo contacto.
import { Search } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { Input } from '@/kit';
import { cn } from '@/lib/cn';
import { PENDING_REASON_LABEL, type ProspectBucket } from '@/domains/sales/wizard/prospects';
import type { PendingReason, ProspectStatus } from '@/domains/sales/wizard/types';
import {
    brandOptions,
    countActiveFilters,
    dayAhead,
    EMPTY_FILTERS,
    toggleInList,
    type ProspectsFilters,
} from '../lib/prospectsFilters';
import { PROSPECT_STATUS_LABEL } from '../lib/prospectsFormat';
import type { ProspectRow } from '../lib/prospectsRows';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD = 'h-[26px] text-[11.5px]';
const SELECT =
    'h-[26px] w-full rounded-md border border-input bg-transparent px-2 text-[11.5px] outline-none focus-visible:border-ring';
const CHIP =
    'cursor-pointer whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold transition-colors';
const SHORTCUT =
    'cursor-pointer rounded border border-border px-1.5 py-0.5 text-center text-[10px] transition-colors hover:bg-accent';

/** Los chips de la agenda, con el color que el mockup le da a cada tramo. */
const URGENCY_CHIPS: { value: ProspectBucket; label: string; off: string; on: string }[] = [
    {
        value: 'overdue',
        label: 'Vencidos',
        off: 'border-destructive/40 text-destructive',
        on: 'border-destructive bg-destructive text-white',
    },
    {
        value: 'today',
        label: 'Para hoy',
        off: 'border-primary/40 text-primary',
        on: 'border-primary bg-primary text-primary-foreground',
    },
    {
        value: 'upcoming',
        label: 'Próximos',
        off: 'border-border bg-card hover:bg-accent',
        on: 'border-primary bg-primary text-primary-foreground',
    },
];

// El orden es el del ciclo de vida, no el del enum ni el alfabético: del pendiente al resuelto,
// con los estados del sistema donde les toca.
const STATUS_OPTIONS: ProspectStatus[] = [
    'PENDING',
    'UNDER_REVIEW',
    'READY_TO_RESUME',
    'CLOSED',
    'CANCELLED',
    'LOST',
];

// Los 18 del catálogo, en alfabético por etiqueta. Los de sistema SÍ se filtran: el vendedor no
// puede elegirlos al guardar, pero sí buscar las ventas que se trabaron.
const REASON_OPTIONS = (Object.keys(PENDING_REASON_LABEL) as PendingReason[]).sort((a, b) =>
    PENDING_REASON_LABEL[a].localeCompare(PENDING_REASON_LABEL[b], 'es-MX'),
);

const DATE_SHORTCUTS: { label: string; days: number }[] = [
    { label: 'Hoy', days: 0 },
    { label: '3d', days: 3 },
    { label: '7d', days: 7 },
];

export function ProspectsFiltersPanel({
    filters,
    onChange,
    rows,
    collapsed,
    onToggleCollapsed,
}: {
    filters: ProspectsFilters;
    onChange: (next: ProspectsFilters) => void;
    /** La agenda completa: de ahí salen las marcas que el select de producto puede ofrecer. */
    rows: ProspectRow[];
    collapsed: boolean;
    onToggleCollapsed: () => void;
}) {
    const patch = (partial: Partial<ProspectsFilters>) => {
        onChange({ ...filters, ...partial });
    };
    const activeCount = countActiveFilters(filters);
    const brands = brandOptions(rows);

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeCount}
            onReset={() => {
                onChange(EMPTY_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                    />
                    <Input
                        type="search"
                        value={filters.query}
                        onChange={(e) => {
                            patch({ query: e.target.value });
                        }}
                        placeholder="Cliente, folio o teléfono"
                        aria-label="Buscar prospecto por cliente, folio o teléfono"
                        className={cn(FIELD, 'pl-6')}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Urgencia</div>
                <div className="flex flex-wrap gap-[3px]">
                    {URGENCY_CHIPS.map((chip) => {
                        const on = filters.urgency.includes(chip.value);
                        return (
                            <button
                                key={chip.value}
                                type="button"
                                aria-pressed={on}
                                onClick={() => {
                                    patch({
                                        urgency: toggleInList(filters.urgency, chip.value),
                                    });
                                }}
                                className={cn(CHIP, on ? chip.on : cn(chip.off, 'hover:bg-accent'))}
                            >
                                {chip.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Estado</div>
                <select
                    value={filters.status}
                    onChange={(e) => {
                        patch({ status: e.target.value as ProspectsFilters['status'] });
                    }}
                    aria-label="Filtrar por estado del prospecto"
                    className={SELECT}
                >
                    <option value="">Todos</option>
                    {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                            {PROSPECT_STATUS_LABEL[status]}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Motivo del pending</div>
                <select
                    value={filters.reason}
                    onChange={(e) => {
                        patch({ reason: e.target.value as ProspectsFilters['reason'] });
                    }}
                    aria-label="Filtrar por motivo del pending"
                    className={SELECT}
                >
                    <option value="">Todos</option>
                    {REASON_OPTIONS.map((reason) => (
                        <option key={reason} value={reason}>
                            {PENDING_REASON_LABEL[reason]}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Producto de interés</div>
                <select
                    value={filters.brand}
                    disabled={brands.length === 0}
                    onChange={(e) => {
                        patch({ brand: e.target.value });
                    }}
                    aria-label="Filtrar por marca del producto de interés"
                    title={
                        brands.length === 0
                            ? 'Ningún prospecto tiene todavía un producto del catálogo'
                            : undefined
                    }
                    className={cn(SELECT, 'disabled:cursor-not-allowed disabled:opacity-50')}
                >
                    <option value="">Todos</option>
                    {brands.map((brand) => (
                        <option key={brand} value={brand}>
                            {brand}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Próximo contacto</div>
                <div className="mb-1 grid grid-cols-2 gap-1">
                    <Input
                        type="date"
                        value={filters.from}
                        onChange={(e) => {
                            patch({ from: e.target.value });
                        }}
                        aria-label="Próximo contacto desde"
                        className={FIELD}
                    />
                    <Input
                        type="date"
                        value={filters.to}
                        onChange={(e) => {
                            patch({ to: e.target.value });
                        }}
                        aria-label="Próximo contacto hasta"
                        className={FIELD}
                    />
                </div>
                <div className="grid grid-cols-4 gap-1">
                    {DATE_SHORTCUTS.map((shortcut) => (
                        <button
                            key={shortcut.label}
                            type="button"
                            onClick={() => {
                                patch({ from: dayAhead(0), to: dayAhead(shortcut.days) });
                            }}
                            className={SHORTCUT}
                        >
                            {shortcut.label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => {
                            patch({ from: '', to: '' });
                        }}
                        className={SHORTCUT}
                    >
                        Todo
                    </button>
                </div>
            </div>
        </FiltersColumn>
    );
}
